import { dispatchMobileAction } from "./actions";
import { App } from "@capacitor/app";
import { EpicMobileApi } from "./api";
import { createNativeApiFetch } from "./nativeHttp";
import { EPIC_MOBILE_PUBLIC_ORIGIN, NATIVE_GUEST_FIXTURE, parseNativeAuthCallback, resolveMobileRequestOrigin } from "./environment";
import { renderList as renderSocialWidgets } from "../../public/shared/socials/socialLinkWidget.js";
import { renderIcon } from "./icons";
import { createNativeBridge, shouldProtectScreen } from "./nativeBridge";
import { renderApp } from "./render";
import { captureInboxViewport, restoreInboxViewport } from "./inboxViewport";
import { createSmsSyncDialog, smsReaderAvailable } from "./smsSyncDialog";
import { createAccountBlocksDialog } from "./accountBlocksDialog";
import { createAccountProfileView } from "./accountProfileView";
import { createMobileStore } from "./state";
import { isSupportedMobileUiCompanionField } from "./uiConfig";
import type { MobileAppState } from "./state";
import type { InboxDraftRejectionReason } from "./types";

const root = document.querySelector<HTMLElement>("[data-mobile-root]");
root?.addEventListener("error", (event) => {
  const media = event.target;
  if (media instanceof HTMLElement && media.matches("[data-inbox-media-preview], [data-mobile-inbox-media-preview]")) {
    media.closest(".inbox-media-preview")?.classList.add("is-unavailable");
  }
}, true);
const nativeBridge = createNativeBridge();
const nativeOrigin = resolveMobileRequestOrigin(nativeBridge.platform);
const api = new EpicMobileApi({
  apiPrefix: nativeBridge.apiPrefix,
  mobileGateway: nativeBridge.isNative,
  fetchImpl: nativeOrigin ? createNativeApiFetch(nativeBridge.apiPrefix, nativeOrigin) : undefined
});
const store = createMobileStore(api, { googleCredentialProvider: nativeBridge });
const smsSyncDialog = createSmsSyncDialog(api);
let pendingBlockRecovery: { owner: string; target: string } | null = null;
function recoverAccountBlocking(reason: "sign-in" | "account-changed", target = "") {
  pendingBlockRecovery = reason === "sign-in" ? { owner: store.getState().activeAccount?.username || "", target } : null;
  // Existing session restoration owns the normal sign-in UI. No write is queued.
  void store.loadSession();
}
const accountBlocksDialog = createAccountBlocksDialog(api, { onRecover: recoverAccountBlocking });
const accountProfileView = createAccountProfileView(api, { onRecover: recoverAccountBlocking, ...(nativeBridge.isNative ? { registerBack: (close: () => void) => App.addListener("backButton", close) } : {}) });
const LIVE_REFRESH_INTERVAL_MS = 15000;
const scrollPositions = new Map<string, number>();
let nativeAppActive = true;
const screenNeedsProtection = (state: MobileAppState) => shouldProtectScreen(state.sessionState, nativeAppActive, document.visibilityState !== "hidden");

async function completeNativeAuthentication(url: string) {
  if (!nativeBridge.isNative || (nativeBridge.platform !== "android" && nativeBridge.platform !== "ios")) return;
  try {
    const callback = parseNativeAuthCallback(url, nativeBridge.platform, nativeBridge.authClient);
    if (!callback) return;
    await api.exchangeNativeAuth(callback.code, nativeBridge.platform, callback.client);
  } catch (_) {
    // The normal session refresh below returns the app to its safe signed-out state.
  }
  await store.loadSession();
}

interface FocusIdentity {
  action: string;
  tab: string;
  conversationRef: string;
  authField: string;
  profileField: string;
  profilePolicy: boolean;
  socialField: string;
  socialIndex: string;
  checkinField: string;
  reviewText: boolean;
  wctekComposer: boolean;
  inboxComposer: boolean;
  inboxDraftText: boolean;
  memoryConsent: string;
  socialEnabled: boolean;
  memoryUploadConsent: boolean;
  memoryFile: boolean;
  inboxFilter: string;
  inboxLabel: string;
  selectionStart: number | null;
  selectionEnd: number | null;
}

function captureScroll(container: HTMLElement) {
  container.querySelectorAll<HTMLElement>("[data-scroll-key]").forEach((scroller) => {
    const key = scroller.dataset.scrollKey;
    if (key) scrollPositions.set(key, scroller.scrollTop);
  });
}

function restoreScroll(container: HTMLElement) {
  container.querySelectorAll<HTMLElement>("[data-scroll-key]").forEach((scroller) => {
    const key = scroller.dataset.scrollKey;
    if (key && scrollPositions.has(key)) scroller.scrollTop = scrollPositions.get(key) || 0;
  });
}

function captureFocus(container: HTMLElement): FocusIdentity | null {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !container.contains(active)) return null;
  return {
    action: active.dataset.mobileAction || "",
    tab: active.dataset.tab || "",
    conversationRef: active.dataset.conversationRef || "",
    authField: active.dataset.mobileAuthField || "",
    profileField: active.dataset.mobileProfileField || "",
    profilePolicy: active.hasAttribute("data-mobile-profile-policy"),
    socialField: active.dataset.mobileSocialField || "",
    socialIndex: active.dataset.socialIndex || "",
    checkinField: active.dataset.mobileCheckinField || "",
    reviewText: active.hasAttribute("data-mobile-review-text"),
    wctekComposer: active.hasAttribute("data-mobile-wctek-composer"),
    inboxComposer: active.hasAttribute("data-mobile-inbox-composer"),
    inboxDraftText: active.hasAttribute("data-mobile-inbox-draft-text"),
    memoryConsent: active.dataset.mobileMemoryConsent || "",
    socialEnabled: active.hasAttribute("data-mobile-social-enabled"),
    memoryUploadConsent: active.hasAttribute("data-mobile-memory-upload-consent"),
    memoryFile: active.hasAttribute("data-mobile-memory-file"),
    inboxFilter: active.dataset.mobileInboxFilter || "",
    inboxLabel: active.hasAttribute("data-mobile-inbox-label") ? (active as HTMLInputElement).value : "",
    selectionStart: active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement ? active.selectionStart : null,
    selectionEnd: active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement ? active.selectionEnd : null
  };
}

function restoreFocus(container: HTMLElement, identity: FocusIdentity | null) {
  if (!identity) return;
  let match: HTMLElement | null | undefined = null;
  if (identity.authField) {
    match = [...container.querySelectorAll<HTMLElement>("[data-mobile-auth-field]")].find((control) => control.dataset.mobileAuthField === identity.authField);
  } else if (identity.profileField) {
    match = [...container.querySelectorAll<HTMLElement>("[data-mobile-profile-field]")].find((control) => control.dataset.mobileProfileField === identity.profileField);
  } else if (identity.profilePolicy) {
    match = container.querySelector<HTMLElement>("[data-mobile-profile-policy]");
  } else if (identity.socialField) {
    match = [...container.querySelectorAll<HTMLElement>("[data-mobile-social-field]")].find((control) => control.dataset.mobileSocialField === identity.socialField && (control.dataset.socialIndex || "") === identity.socialIndex);
  } else if (identity.checkinField) {
    match = [...container.querySelectorAll<HTMLElement>("[data-mobile-checkin-field]")].find((control) => control.dataset.mobileCheckinField === identity.checkinField);
  } else if (identity.reviewText) {
    match = container.querySelector<HTMLElement>("[data-mobile-review-text]");
  } else if (identity.wctekComposer) {
    match = container.querySelector<HTMLElement>("[data-mobile-wctek-composer]");
  } else if (identity.inboxComposer) {
    match = container.querySelector<HTMLElement>("[data-mobile-inbox-composer]");
  } else if (identity.inboxDraftText) {
    match = container.querySelector<HTMLElement>("[data-mobile-inbox-draft-text]");
  } else if (identity.memoryConsent) {
    match = [...container.querySelectorAll<HTMLElement>("[data-mobile-memory-consent]")].find((control) => control.dataset.mobileMemoryConsent === identity.memoryConsent);
  } else if (identity.socialEnabled) {
    match = container.querySelector<HTMLElement>("[data-mobile-social-enabled]");
  } else if (identity.memoryUploadConsent) {
    match = container.querySelector<HTMLElement>("[data-mobile-memory-upload-consent]");
  } else if (identity.memoryFile) {
    match = container.querySelector<HTMLElement>("[data-mobile-memory-file]");
  } else if (identity.inboxFilter) {
    match = [...container.querySelectorAll<HTMLElement>("[data-mobile-inbox-filter]")].find((control) => control.dataset.mobileInboxFilter === identity.inboxFilter);
  } else if (identity.inboxLabel) {
    match = [...container.querySelectorAll<HTMLInputElement>("[data-mobile-inbox-label]")].find((control) => control.value === identity.inboxLabel);
  } else {
    match = [...container.querySelectorAll<HTMLElement>("[data-mobile-action]")].find((control) => (
      control.dataset.mobileAction === identity.action
      && (control.dataset.tab || "") === identity.tab
      && (control.dataset.conversationRef || "") === identity.conversationRef
    ));
  }
  match?.focus({ preventScroll: true });
  if (
    match &&
    (match instanceof HTMLInputElement || match instanceof HTMLTextAreaElement) &&
    identity.selectionStart !== null &&
    identity.selectionEnd !== null
  ) {
    match.setSelectionRange(identity.selectionStart, identity.selectionEnd);
  }
}

interface AuthDomDraft {
  values: Record<string, string>;
  passwordVisible: boolean;
}

function captureAuthDomDraft(container: HTMLElement): AuthDomDraft | null {
  const fields = [...container.querySelectorAll<HTMLInputElement>("[data-mobile-auth-field]")];
  if (!fields.length) return null;
  return {
    values: Object.fromEntries(fields.map((field) => [field.dataset.mobileAuthField || "", field.value])),
    passwordVisible: container.querySelector<HTMLInputElement>("#mobile-auth-password")?.type === "text"
  };
}

function restoreAuthDomDraft(container: HTMLElement, draft: AuthDomDraft | null) {
  if (!draft) return;
  container.querySelectorAll<HTMLInputElement>("[data-mobile-auth-field]").forEach((field) => {
    const key = field.dataset.mobileAuthField || "";
    if (Object.prototype.hasOwnProperty.call(draft.values, key)) field.value = draft.values[key];
  });
  const password = container.querySelector<HTMLInputElement>("#mobile-auth-password");
  const toggle = container.querySelector<HTMLButtonElement>("[data-mobile-auth-password-toggle]");
  if (password && toggle && draft.passwordVisible) {
    password.type = "text";
    toggle.setAttribute("aria-pressed", "true");
    const label = toggle.dataset.hideLabel || "Hide password";
    toggle.setAttribute("aria-label", label);
    toggle.setAttribute("title", label);
    toggle.innerHTML = renderIcon("eye-off");
  }
}

function applyPresentation(container: HTMLElement, state: MobileAppState) {
  container.dataset.mobileAccent = state.ui.theme.accent;
  container.dataset.mobileAppearance = state.ui.theme.appearance;
  container.dataset.mobileDensity = state.ui.theme.density;
  container.dataset.mobileUiRevision = state.ui.revision;
  container.dataset.mobileSessionState = state.sessionState;
}

function actionControl(event: Event) {
  const target = event.target;
  return target instanceof Element ? target.closest<HTMLElement>("[data-mobile-action]") : null;
}

function bindActions(container: HTMLElement) {
  let composingField = false;
  let lastHistoryAttempt = "";
  let touchY: number | null = null;
  function loadHistoryAtTop(target: EventTarget | null) {
    const thread = target instanceof Element ? target.closest<HTMLElement>("[data-scroll-key='inbox-thread-messages']") : null;
    const state = store.getState();
    if (!thread || thread.scrollTop > 100 || state.inbox.historyState === "loading" || state.inbox.historyError || !state.online) return;
    const cursor = state.inbox.messagePage?.nextCursor;
    const key = `${state.accountVersion}:${state.inbox.selectedConversation?.conversationRef}:${cursor}`;
    if (!cursor || key === lastHistoryAttempt) return;
    lastHistoryAttempt = key;
    void store.loadEarlierMessages();
  }
  container.addEventListener("wheel", event => {
    if (event.deltaY < 0) loadHistoryAtTop(event.target);
  }, { passive: true });
  container.addEventListener("touchstart", event => { touchY = event.touches[0]?.clientY ?? null; }, { passive: true });
  container.addEventListener("touchmove", event => {
    const nextY = event.touches[0]?.clientY ?? null;
    if (touchY !== null && nextY !== null && nextY > touchY) loadHistoryAtTop(event.target);
    touchY = nextY;
  }, { passive: true });
  container.addEventListener("keydown", event => {
    if (["ArrowUp", "PageUp", "Home"].includes(event.key) && !(event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)) loadHistoryAtTop(event.target);
  });
  container.addEventListener("scroll", event => {
    const thread = event.target;
    if (thread instanceof HTMLElement && thread.dataset.scrollKey === "inbox-thread-messages" && thread.scrollTop > 100) lastHistoryAttempt = "";
  }, true);
  function updateTextControl(target: HTMLInputElement | HTMLTextAreaElement) {
    const profileField = target.dataset.mobileProfileField;
    if (isSupportedMobileUiCompanionField(profileField)) {
      store.updateCompanionSetting(profileField, target.value);
      return true;
    }
    const socialField = target.dataset.mobileSocialField;
    const socialIndex = Number(target.dataset.socialIndex);
    if ((socialField === "label" || socialField === "url") && Number.isInteger(socialIndex) && socialIndex >= 0) {
      store.updateSocialLink(socialIndex, socialField, target.value);
      return true;
    }
    const checkinField = target.dataset.mobileCheckinField;
    if (checkinField) {
      store.updateCheckinSetting(checkinField, target.value);
      return true;
    }
    if (target.hasAttribute("data-mobile-review-text")) {
      store.updateReviewDraftText(target.value);
      return true;
    }
    if (target.hasAttribute("data-mobile-wctek-composer")) {
      store.updateWctekComposer(target.value);
      return true;
    }
    if (target.hasAttribute("data-mobile-inbox-composer")) {
      store.updateInboxComposer(target.value);
      return true;
    }
    if (target.hasAttribute("data-mobile-inbox-draft-text")) {
      store.updateInboxDraftReviewText(target.value);
      return true;
    }
    if (target.dataset.mobileInboxFilter === "search") {
      store.updateInboxFilter("search", target.value);
      return true;
    }
    return false;
  }
  container.addEventListener("click", (event) => {
    const target = event.target;
    const peerProfile = target instanceof Element ? target.closest<HTMLElement>("[data-mobile-user-profiles-open], [data-mobile-account-profile]") : null;
    if (peerProfile) {
      event.preventDefault(); accountProfileView.open(peerProfile.dataset.mobileAccountProfile || ""); return;
    }
    const profileLink = target instanceof Element
      ? target.closest<HTMLAnchorElement>("[data-mobile-profile-link], [data-mobile-profile-socials] a") : null;
    if (profileLink) {
      event.preventDefault();
      void nativeBridge.openProfileLink(profileLink.href);
      return;
    }
    const passwordToggle = target instanceof Element
      ? target.closest<HTMLButtonElement>("[data-mobile-auth-password-toggle]")
      : null;
    if (passwordToggle) {
      const input = container.querySelector<HTMLInputElement>("#mobile-auth-password");
      if (!input) return;
      const showing = input.type === "text";
      input.type = showing ? "password" : "text";
      passwordToggle.setAttribute("aria-pressed", String(!showing));
      const label = showing
        ? passwordToggle.dataset.showLabel || "Show password"
        : passwordToggle.dataset.hideLabel || "Hide password";
      passwordToggle.setAttribute("aria-label", label);
      passwordToggle.setAttribute("title", label);
      passwordToggle.innerHTML = renderIcon(showing ? "eye" : "eye-off");
      input.focus({ preventScroll: true });
      return;
    }
    if (target instanceof Element && target.closest("[data-mobile-auth-back]")) {
      store.cancelSecondFactor();
      return;
    }
    const composerMode = target instanceof Element
      ? target.closest<HTMLButtonElement>("[data-mobile-inbox-composer-mode]")?.dataset.mobileInboxComposerMode
      : null;
    if (composerMode === "reply" || composerMode === "note") {
      void nativeBridge.impact();
      store.setInboxComposerMode(composerMode);
      return;
    }
    const systemPath = target instanceof Element
      ? target.closest<HTMLElement>("[data-mobile-system-path]")?.dataset.mobileSystemPath
      : null;
    if (systemPath && nativeBridge.isNative) {
      event.preventDefault();
      if (systemPath.startsWith("/login/") && nativeBridge.supportsNativeGoogleSignIn) {
        void nativeBridge.impact();
        void store.signInWithGoogle();
        return;
      }
      void nativeBridge.openSystemPath(systemPath);
      return;
    }
    const control = actionControl(event);
    if (!control || control instanceof HTMLSelectElement) return;
    dispatchMobileAction(control.dataset.mobileAction, control, { store, nativeBridge });
  });
  container.addEventListener("compositionstart", (event) => {
    const target = event.target;
    if (target instanceof HTMLElement && (
      target.hasAttribute("data-mobile-profile-field")
      || target.hasAttribute("data-mobile-social-field")
      || target.hasAttribute("data-mobile-checkin-field")
      || target.hasAttribute("data-mobile-review-text")
      || target.hasAttribute("data-mobile-wctek-composer")
      || target.hasAttribute("data-mobile-inbox-composer")
      || target.hasAttribute("data-mobile-inbox-draft-text")
      || target.dataset.mobileInboxFilter === "search"
    )) composingField = true;
  });
  container.addEventListener("compositionend", (event) => {
    composingField = false;
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;
    updateTextControl(target);
  });
  container.addEventListener("input", (event) => {
    if (composingField || (event instanceof InputEvent && event.isComposing)) return;
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;
    updateTextControl(target);
  });
  container.addEventListener("change", (event) => {
    const target = event.target;
    if (target instanceof HTMLSelectElement) {
      const field = target.dataset.mobileProfileField;
      if (isSupportedMobileUiCompanionField(field)) {
        store.updateCompanionSetting(field, target.value);
        return;
      }
      const socialIndex = Number(target.dataset.socialIndex);
      if (target.dataset.mobileSocialField === "platform" && Number.isInteger(socialIndex) && socialIndex >= 0) {
        store.updateSocialLink(socialIndex, "platform", target.value);
        return;
      }
      if (target.dataset.mobileCheckinField) {
        store.updateCheckinSetting(target.dataset.mobileCheckinField, target.value);
        return;
      }
      if (target.hasAttribute("data-mobile-inbox-draft-reason")) {
        store.setInboxDraftRejectionReason(target.value as InboxDraftRejectionReason);
        return;
      }
      const inboxFilter = target.dataset.mobileInboxFilter;
      if (inboxFilter === "channel" || inboxFilter === "status" || inboxFilter === "sort") {
        store.updateInboxFilter(inboxFilter, target.value);
        return;
      }
    }
    if (target instanceof HTMLInputElement) {
      if (target.hasAttribute("data-mobile-profile-policy")) {
        store.setCompanionPolicyAccepted(target.checked);
        return;
      }
      if (target.hasAttribute("data-mobile-social-enabled")) {
        store.setSocialLinksEnabled(target.checked);
        return;
      }
      const socialIndex = Number(target.dataset.socialIndex);
      if (target.dataset.mobileSocialField === "visible" && Number.isInteger(socialIndex) && socialIndex >= 0) {
        store.updateSocialLink(socialIndex, "visible", target.checked);
        return;
      }
      if (target.dataset.mobileCheckinField) {
        store.updateCheckinSetting(target.dataset.mobileCheckinField, target.type === "checkbox" ? target.checked : target.value);
        return;
      }
      const memoryConsent = target.dataset.mobileMemoryConsent;
      if (memoryConsent === "styleLearningEnabled" || memoryConsent === "conversationalUploadEnabled") {
        store.updateMemoryConsent(memoryConsent, target.checked);
        return;
      }
      if (target.hasAttribute("data-mobile-memory-file")) {
        store.selectMemoryUploadFile(target.files?.[0] || null);
        return;
      }
      if (target.hasAttribute("data-mobile-memory-upload-consent")) {
        store.setMemoryUploadConsent(target.checked);
        return;
      }
      if (target.dataset.mobileInboxFilter === "folder") {
        store.updateInboxFilter("folder", target.value);
        return;
      }
      if (target.hasAttribute("data-mobile-inbox-label")) {
        store.toggleInboxLabel(target.value, target.checked);
        return;
      }
      if (target.hasAttribute("data-mobile-inbox-draft-confirm")) {
        store.setInboxDraftSendConfirmed(target.checked);
        return;
      }
    }
    const control = actionControl(event);
    if (!control) return;
    dispatchMobileAction(control.dataset.mobileAction, control, { store, nativeBridge });
  });
  container.addEventListener("submit", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLFormElement)) return;
    if (target.hasAttribute("data-mobile-auth-form")) {
      event.preventDefault();
      const fields = new FormData(target);
      void nativeBridge.impact();
      void store.signIn(String(fields.get("username") || ""), String(fields.get("password") || ""));
      return;
    }
    if (target.hasAttribute("data-mobile-auth-two-factor-form")) {
      event.preventDefault();
      const fields = new FormData(target);
      void nativeBridge.impact();
      void store.verifySecondFactor(String(fields.get("code") || ""));
      return;
    }
    if (target.hasAttribute("data-mobile-inbox-draft-review-form")) {
      event.preventDefault();
      const submitter = (event as SubmitEvent).submitter;
      const action = submitter instanceof HTMLButtonElement ? submitter.dataset.mobileInboxDraftAction : null;
      if (action !== "approve" && action !== "reject" && action !== "send") return;
      void nativeBridge.impact();
      void store.submitInboxDraftReview(action);
      return;
    }
    const submit = target.hasAttribute("data-mobile-profile-form")
      ? () => store.saveCompanionSettings()
      : target.hasAttribute("data-mobile-social-links-form")
        ? () => store.saveSocialLinks()
        : target.hasAttribute("data-mobile-checkin-form")
          ? () => store.saveCheckinSettings()
          : target.hasAttribute("data-mobile-review-form")
            ? () => store.saveReviewDraft()
            : target.hasAttribute("data-mobile-memory-consent-form")
              ? () => store.saveMemoryConsent()
              : target.hasAttribute("data-mobile-memory-upload-form")
              ? () => store.uploadMemoryFile()
              : target.hasAttribute("data-mobile-wctek-form")
                ? () => store.sendWctekMessage()
                : target.hasAttribute("data-mobile-inbox-composer-form")
                  ? () => store.submitInboxComposer()
                : target.hasAttribute("data-mobile-inbox-filter-form")
                  ? () => store.applyInboxFilters()
                : null;
    if (!submit) return;
    event.preventDefault();
    void nativeBridge.impact();
    void submit();
  });
}

async function registerServiceWorker() {
  if (nativeBridge.isNative) return;
  if (!("serviceWorker" in navigator)) return;
  if (location.protocol !== "https:" && location.hostname !== "localhost" && location.hostname !== "127.0.0.1") return;
  try {
    await navigator.serviceWorker.register("/mobile/sw.js", { scope: "/mobile/" });
  } catch (error) {
    console.warn("[mobile] service worker registration failed", error);
  }
}

if (root) {
  bindActions(root);
  root.addEventListener("error", (event) => {
    const image = event.target;
    if (image instanceof HTMLImageElement && image.parentElement?.classList.contains("account-page__avatar")) image.hidden = true;
  }, true);
  let lastWctekSessionId: string | null = null;
  let lastWctekMessageCount = 0;
  let lastInboxFiltersOpen = false;
  let lastAuthStage: MobileAppState["auth"]["stage"] | null = null;
  let lastInboxTimeline: { reference: string; first: string | null; last: string | null; count: number } | null = null;
  let lastAccountVersion = store.getState().accountVersion;
  let renderRevision = 0;
  let sensitiveMode: boolean | null = null;
  store.subscribe((state) => {
    const revision = ++renderRevision;
    if (lastAccountVersion !== state.accountVersion) {
      scrollPositions.clear();
      lastInboxTimeline = null;
      lastAccountVersion = state.accountVersion;
    }
    const smsIdentity = state.session?.identity?.authIdentity || state.session?.authUser;
    accountBlocksDialog.sync(state.activeAccount?.username || "", String(smsIdentity?.id || ""), state.sessionState === "ready");
    accountProfileView.sync(state.activeAccount?.username || "", String(smsIdentity?.id || ""), state.sessionState === "ready", state.online);
    // A session refresh retains its account while loading. Clearing the native
    // binding here would revoke an otherwise current SMS run on every resume.
    const smsAccountBound = state.sessionState === "ready" || state.sessionState === "loading" && Boolean(state.session);
    // Cold startup has not resolved the saved session yet. Keep the native
    // background binding until an actual account or signed-out result arrives.
    if (state.sessionState !== "loading" || state.session) {
      smsSyncDialog.setAccount(smsAccountBound ? state.activeAccount?.username || "" : "", String(smsIdentity?.id || ""));
    }
    const nextSensitiveMode = screenNeedsProtection(state);
    if (nextSensitiveMode !== sensitiveMode) {
      sensitiveMode = nextSensitiveMode;
      void nativeBridge.setSensitiveMode(nextSensitiveMode);
    }
    const wctekMessageCount = state.sessionState === "unauthenticated" ? state.guest.messages.length : state.wctek.messages.length;
    const pinWctekToLatest = state.activeTab === "olivia" && (
      state.wctek.sessionId !== lastWctekSessionId
      || wctekMessageCount > lastWctekMessageCount
    );
    const focusInboxFilters = state.inbox.filtersOpen && !lastInboxFiltersOpen;
    const returnToInboxFilters = !state.inbox.filtersOpen && lastInboxFiltersOpen;
    const focusAuthStage = state.sessionState === "unauthenticated"
      && lastAuthStage !== null
      && state.auth.stage !== lastAuthStage;
    const previousThread = root.querySelector<HTMLElement>("[data-scroll-key='inbox-thread-messages']");
    const threadSnapshot = previousThread ? captureInboxViewport(previousThread) : null;
    const nextTimeline = state.inbox.detailState === "ready" && state.inbox.selectedConversation ? {
      reference: state.inbox.selectedConversation.conversationRef,
      first: state.inbox.messages[0]?.messageRef || null,
      last: state.inbox.messages[state.inbox.messages.length - 1]?.messageRef || null,
      count: state.inbox.messages.length
    } : null;
    const newThread = nextTimeline && nextTimeline.reference !== lastInboxTimeline?.reference;
    const followLatest = nextTimeline && threadSnapshot?.atLatest && nextTimeline.last !== lastInboxTimeline?.last;
    captureScroll(root);
    const focus = captureFocus(root);
    const authDraft = captureAuthDomDraft(root);
    applyPresentation(root, state);
    root.innerHTML = renderApp(state);
    if (state.sessionState === "ready" && pendingBlockRecovery) {
      const recovery = pendingBlockRecovery; pendingBlockRecovery = null;
      if (recovery.owner.toLowerCase() === state.activeAccount?.username.toLowerCase()) {
        queueMicrotask(() => {
          if (store.getState().sessionState !== "ready" || store.getState().activeAccount?.username.toLowerCase() !== recovery.owner.toLowerCase()) return;
          if (recovery.target) accountProfileView.open(recovery.target); else accountBlocksDialog.open();
        });
      }
    }
    root.querySelectorAll<HTMLButtonElement>("[data-mobile-blocks-open]").forEach(button => button.addEventListener("click", () => accountBlocksDialog.open()));
    if (smsReaderAvailable()) {
      for (const smsButton of root.querySelectorAll<HTMLButtonElement>("[data-mobile-sms-open]")) {
        smsButton.hidden = false;
        smsButton.addEventListener("click", () => smsSyncDialog.open());
      }
    }
    const smsReplyButton = root.querySelector<HTMLButtonElement>("[data-mobile-sms-reply]");
    if (smsReplyButton && smsReaderAvailable() && state.inbox.selectedConversation) {
      smsReplyButton.hidden = false;
      const reference = state.inbox.selectedConversation.conversationRef;
      smsReplyButton.addEventListener("click", () => smsSyncDialog.reply(reference));
    }
    const profileSocials = root.querySelector<HTMLElement>("[data-mobile-profile-socials]");
    if (profileSocials && state.publicProfile.data) {
      renderSocialWidgets(profileSocials, state.publicProfile.data.socialLinks, {
        accountUsername: state.publicProfile.data.username,
        assetOrigin: EPIC_MOBILE_PUBLIC_ORIGIN,
        emptyText: "No socials yet."
      });
    }
    restoreAuthDomDraft(root, authDraft);
    restoreFocus(root, focus);
    restoreScroll(root);
    const currentThread = root.querySelector<HTMLElement>("[data-scroll-key='inbox-thread-messages']");
    if (currentThread) restoreInboxViewport(currentThread, threadSnapshot, Boolean(newThread || followLatest));
    window.requestAnimationFrame(() => {
      if (revision !== renderRevision) return;
      if (focusInboxFilters) {
        root.querySelector<HTMLElement>("[data-inbox-filter-close]")?.focus({ preventScroll: true });
      } else if (returnToInboxFilters) {
        root.querySelector<HTMLElement>("[data-mobile-action='toggle-inbox-filters']")?.focus({ preventScroll: true });
      }
      if (focusAuthStage) {
        root.querySelector<HTMLElement>(state.auth.stage === "two-factor" ? "#mobile-auth-code" : "#mobile-auth-username")?.focus({ preventScroll: true });
      }
      if (pinWctekToLatest) {
        const conversation = root.querySelector<HTMLElement>("[data-scroll-key='wctek-messages']");
        if (conversation) conversation.scrollTop = conversation.scrollHeight;
      }
    });
    lastWctekSessionId = state.wctek.sessionId;
    lastWctekMessageCount = wctekMessageCount;
    lastInboxFiltersOpen = state.inbox.filtersOpen;
    lastAuthStage = state.sessionState === "unauthenticated" ? state.auth.stage : null;
    lastInboxTimeline = nextTimeline;
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Enter" && !event.shiftKey && !event.isComposing && event.target instanceof HTMLTextAreaElement
      && event.target.hasAttribute("data-guest-composer")) {
      event.preventDefault();
      void store.sendWctekMessage();
      return;
    }
    if (event.key === "Escape" && store.getState().inbox.filtersOpen) {
      event.preventDefault();
      store.toggleInboxFilters(false);
      return;
    }
    if (event.key === "Tab" && store.getState().inbox.filtersOpen) {
      const sheet = root.querySelector<HTMLElement>(".inbox-filter-sheet");
      const controls = sheet
        ? [...sheet.querySelectorAll<HTMLElement>("button:not([disabled]), input:not([disabled]), select:not([disabled])")]
          .filter((control) => control.offsetParent !== null)
        : [];
      if (!controls.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });
  window.addEventListener("online", () => store.setOnline(true));
  window.addEventListener("offline", () => store.setOnline(false));
  document.addEventListener("visibilitychange", () => {
    void nativeBridge.setSensitiveMode(screenNeedsProtection(store.getState()));
    if (document.visibilityState === "visible" && store.getState().sessionState === "ready") {
      void store.refreshLiveInbox();
    }
  });
  void nativeBridge.initialize();
  void nativeBridge.onAppStateChange((isActive) => {
    nativeAppActive = isActive;
    void nativeBridge.setSensitiveMode(screenNeedsProtection(store.getState()));
    if (!isActive) {
      accountProfileView.close();
      return;
    }
    accountBlocksDialog.refresh();
    accountProfileView.refresh();
    void store.resumeSession();
  });
  void nativeBridge.onAppUrlOpen((url) => {
    void completeNativeAuthentication(url);
  });
  window.setInterval(() => {
    const state = store.getState();
    if (
      document.visibilityState === "visible" &&
      state.sessionState === "ready" &&
      state.online !== false
    ) {
      void store.refreshLiveInbox();
    }
  }, LIVE_REFRESH_INTERVAL_MS);
  window.setInterval(() => store.refreshGuestDisplay(), 1000);
  void (async () => {
    if (NATIVE_GUEST_FIXTURE && (!nativeBridge.isNative || (await App.getInfo()).id !== "pro.ctek123.epic.dev")) {
      root.textContent = "This isolated test build requires EPIC Dev.";
      return;
    }
    void store.loadUiConfig();
    void store.loadSession();
  })();
  void registerServiceWorker();
}
